import { useGridApiRef } from '@mui/x-data-grid-pro';
import type { InvoiceOverview, InvoicesHighlightsSummary } from '@vy/api-definition';
import { CurrencyExchangeRates, INVOICE_PDF_TYPES } from '@vy/types';
import { isEmptyArray, isNotNullOrUndefined } from '@vy/utils';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { InvoicesPageContent } from '../components';
import { useInvoicesActions } from '../hooks/use-invoices-actions';
import { useInvoicesData } from '../hooks/use-invoices-data';
import { useInvoicesFilters } from '../hooks/use-invoices-filters';
import { useFilteredSyncSelectedInvoices } from '../hooks/use-selected-invoices-sync-filter';
import type {
  InvoicesActions,
  InvoicesDisabledStates,
  InvoicesPageData,
  InvoicesSyncDialogs,
  InvoicesTableConfig,
} from '../types/invoices-page-types';

type InvoicesPageContentContainerProps = {
  invoices: InvoiceOverview[];
  invoicesCount: number;
  loadingInvoices?: boolean;
  invoicesHighlightsSummary?: InvoicesHighlightsSummary;
  exchangeRates?: CurrencyExchangeRates;
  availableCustomFieldNames?: string[];
  page?: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
};

export const InvoicesPageContentContainer: React.FC<InvoicesPageContentContainerProps> = ({
  invoices,
  invoicesCount,
  loadingInvoices = false,
  invoicesHighlightsSummary,
  exchangeRates,
  availableCustomFieldNames,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) => {
  const [addSyncInvoicesDialogOpen, setAddSyncInvoicesDialogOpen] = useState<boolean>(false);
  const [addSyncInvoicesAsSalesOrderDialogOpen, setAddSyncInvoicesAsSalesOrderDialogOpen] =
    useState<boolean>(false);

  const navigate = useNavigate();
  const apiRef = useGridApiRef();

  // Data management
  const { selectedInvoices, setSelectedInvoices, connectedIntegrations, resetInvoicesQuery, hasUsageEventsReportConfiguration } =
    useInvoicesData();

  // Filtering logic
  const filters = useInvoicesFilters();

  // Filtered invoices for sync operations
  const filteredApprovedSelectedInvoices = useFilteredSyncSelectedInvoices(selectedInvoices);

  // Actions with success callback
  const actions = useInvoicesActions({
    selectedInvoices,
    apiRef,
    onSuccess: resetInvoicesQuery,
  });

  const handleZenModeClick = () => {
    navigate(`/invoices/monthly-approval?invoiceId=${invoices[0].id}`);
  };

  const handleSyncToNetSuiteOpen = () => {
    setAddSyncInvoicesDialogOpen(true);
  };

  const handleSyncToNetSuiteClose = () => {
    setAddSyncInvoicesDialogOpen(false);
  };

  const handleSyncToNetSuite = async () => {
    if (isNotNullOrUndefined(filteredApprovedSelectedInvoices)) {
      await actions.handleSyncToNetSuite(filteredApprovedSelectedInvoices);
    }
  };

  const toggleSyncToNetSuiteSalesOrderDialog = (open: boolean) => {
    setAddSyncInvoicesAsSalesOrderDialogOpen(open);
  }

  const handleSyncToNetSuiteSalesOrder = async () => {
    if (isNotNullOrUndefined(filteredApprovedSelectedInvoices)) {
      await actions.handleSyncToNetSuiteSalesOrder(filteredApprovedSelectedInvoices);
    }
  };

  const data: InvoicesPageData = {
    invoices,
    invoicesCount,
    selectedInvoices,
    loadingInvoices,
    invoicesHighlightsSummary,
    connectedIntegrations,
    exchangeRates,
    availableCustomFieldNames,
    hasUsageEventsReportConfiguration,
  };

  const tableConfig: InvoicesTableConfig = {
    apiRef,
    onInvoicesSelect: setSelectedInvoices,
    page,
    pageSize,
    onPageChange,
    onPageSizeChange,
  };

  const actionsObj: InvoicesActions = {
    onApproveInvoices: actions.handleApproveSelectedInvoices,
    onExportSelectedInvoices: actions.handleExportSelectedInvoices,
    onDownloadAsPdf: () => actions.handleDownloadInvoicesPdfs(INVOICE_PDF_TYPES.Light),
    onDownloadAsPdfStatement: () => actions.handleDownloadInvoicesPdfs(INVOICE_PDF_TYPES.Statement),
    onUpdateInvoicesBillingStatus: actions.handleUpdateInvoicesBillingStatus,
    onSyncToStripe: actions.handleSyncToStripe,
    onSyncToQuickBooks: actions.handleSyncToQuickBooks,
    onSyncToMorning: actions.handleSyncToMorning,
    onSyncToMarketplace: actions.handleSyncToMarketplace,
    onSyncToXero: actions.handleSyncToXero,
    onSyncToPaddle: actions.handleSyncToPaddle,
    onZenModeClick: handleZenModeClick,
    onDownloadUsageEventsReport: actions.handleDownloadUsageEventsReports,
  };

  const syncDialogs: InvoicesSyncDialogs = {
    invoice: {
      open: addSyncInvoicesDialogOpen,
      filteredApprovedInvoices: filteredApprovedSelectedInvoices,
      onOpen: handleSyncToNetSuiteOpen,
      onClose: handleSyncToNetSuiteClose,
      onSync: handleSyncToNetSuite,
    },
    salesOrder: {
      open: addSyncInvoicesAsSalesOrderDialogOpen,
      onOpen: () => toggleSyncToNetSuiteSalesOrderDialog(true),
      onClose: () => toggleSyncToNetSuiteSalesOrderDialog(false),
      onSync: handleSyncToNetSuiteSalesOrder,
    },
  };

  const disabledStates: InvoicesDisabledStates = {
    syncToNetSuiteDisabled: isEmptyArray(filteredApprovedSelectedInvoices),
    actionsDisabled: isEmptyArray(selectedInvoices),
  };

  return (
    <InvoicesPageContent
      data={data}
      tableConfig={tableConfig}
      filters={filters}
      actions={actionsObj}
      syncDialogs={syncDialogs}
      disabledStates={disabledStates}
    />
  );
};
